
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface NavbarProps {
  variant?: 'transparent' | 'solid';
}

const navItems = [
  { label: 'About Us', path: '/about' },
  { label: 'Relocation', path: '/relocation' },
  { label: 'Rentals', path: '/rentals' },
  { label: 'Services', path: '/services' },
  { label: 'Contact', path: '/#contact' },
];

export default function Navbar({ variant = 'transparent' }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Track scroll position to toggle solid/transparent background
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    // Anchor links (e.g., /#contact) are never "active" in the NavBar sense
    if (path === '/#contact') return false;
    return location.pathname === path;
  };

  const isSolid = variant === 'solid' || isScrolled;

  const linkColor = (path: string) => {
    if (isActive(path)) return 'text-[#A7D0CC]';
    return isSolid ? 'text-black hover:text-[#A7D0CC]' : 'text-white hover:text-[#B4E4E0]';
  };

  const mobileLinkColor = (path: string) => {
    if (isActive(path)) return 'text-[#A7D0CC]';
    return isSolid ? 'text-black hover:text-[#A7D0CC]' : 'text-white hover:text-[#B4E4E0]';
  };

  const renderLink = (item: { label: string; path: string }, isMobile = false) => {
    const colorClass = isMobile ? mobileLinkColor(item.path) : linkColor(item.path);
    const baseClass = `text-[15px] font-medium transition-colors ${colorClass}`;

    // Handle anchor links that should use a regular <a> tag
    if (item.path.startsWith('/#')) {
      return (
        <a
          key={item.path}
          href={item.path}
          className={`${isMobile ? 'block ' : ''}${baseClass}`}
          onClick={() => isMobile && setMobileMenuOpen(false)}
        >
          {item.label}
        </a>
      );
    }

    // Normal internal navigation using react‑router Link
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`${isMobile ? 'block ' : ''}${baseClass}`}
        onClick={() => isMobile && setMobileMenuOpen(false)}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid
          ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.06)]'
          : 'bg-black/60 backdrop-blur-md'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20 py-4 md:py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img
            src="https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/7cc47221362597664001b9fe6feac620.png"
            alt="Miami Comfort Group"
            className={`h-8 md:h-12 w-auto object-contain transition-all duration-300 ${
              isSolid ? '' : 'brightness-0 invert'
            }`}
          />
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-8 xl:gap-12">
          {navItems.map((item) => renderLink(item))}

          {/* Language Switcher */}
          <div
            className={`flex items-center gap-1 ml-4 border-l pl-4 ${
              isSolid ? 'border-black/20' : 'border-white/20'
            }`}
          >
            <span className="px-2 py-1 text-[13px] font-bold text-[#A7D0CC]">EN</span>
            <span className={isSolid ? 'text-black/30' : 'text-white/30'}>|</span>
            <Link
              to="/alquileres"
              className={`px-2 py-1 text-[13px] font-medium transition-colors ${
                isSolid ? 'text-black/60 hover:text-black' : 'text-white/60 hover:text-white'
              }`}
            >
              ES
            </Link>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
        >
          <i
            className={`${
              mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
            } text-2xl ${isSolid ? 'text-black' : 'text-white'}`}
          ></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className={`lg:hidden border-t ${
            isSolid ? 'bg-white border-gray-200' : 'bg-black/95 backdrop-blur-md border-white/10'
          }`}
        >
          <div className="px-6 py-4 space-y-4">
            {navItems.map((item) => renderLink(item, true))}
            <div
              className={`flex items-center gap-3 pt-2 border-t ${
                isSolid ? 'border-gray-200' : 'border-white/10'
              }`}
            >
              <span className="text-[#A7D0CC] text-[14px] font-bold">English</span>
              <Link
                to="/alquileres"
                className={`text-[14px] font-medium ${
                  isSolid ? 'text-black/60 hover:text-black' : 'text-white/60 hover:text-white'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Español
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
